import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import {
  Avatar,
  Chip,
  Typography,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Divider,
  Collapse,
  CircularProgress
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplyIcon from "@mui/icons-material/Reply";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import ForumIcon from "@mui/icons-material/Forum";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useDispatch, useSelector } from "react-redux";
import { setLimit, setPage } from "../../features/training/trainingSlice";
import {
  selectAllQuestions,
  selectLoading,
  selectTotal
} from "../../features/faq/faqSelectors";
import useSignedUrl from "../../utils/useSignedUrl";
import {
  getAllQuestionsData,
  updateQuestionStatus,
  addReplyToQuestion,
  addThreadedReplyToQuestion,
  editThreadedReplyToQuestion,
  deleteReplyToQuestion,
  deleteThreadedReplyToQuestion
} from "../../features/faq/faqSlice";
import getInitials from "../../utils/getInitials";
import dayjs from "dayjs";
import { Delete } from "@mui/icons-material";

const styles = {
  paperContainer: {
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e5e7eb",
    overflow: "hidden"
  },
  tableContainer: {
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px"
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#cbd5e1",
      borderRadius: "6px",
      "&:hover": {
        backgroundColor: "#94a3b8"
      }
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f8fafc",
      borderRadius: "6px"
    }
  },
  headerCell: {
    minWidth: 100,
    background: "rgb(55, 65, 81)",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 600,
    letterSpacing: "0.5px",
    borderBottom: "none",
    position: "sticky",
    top: 0,
    zIndex: 100
  },
  tableRow: {
    fontSize: "13px",
    borderBottom: "1px solid #f1f5f9",
    "&:hover": {
      backgroundColor: "#f8fafc !important",
      transform: "translateY(-1px)",
      transition: "all 0.2s ease-in-out"
    }
  },
  cellContent: {
    padding: "16px 12px"
  }
};

const columns = [
  { id: "date", label: "Date", align: "left", minWidth: 120 },
  { id: "user", label: "User", align: "left", minWidth: 180 },
  { id: "question", label: "Question", align: "left", minWidth: 300 },
  { id: "status", label: "Status", align: "center", minWidth: 120 },
  { id: "actions", label: "Actions", align: "center", minWidth: 140 }
];

const statusConfig = {
  Pending: {
    color: "warning",
    bgColor: "#fff3cd",
    textColor: "#856404",
    icon: "⏳"
  },
  Ignored: {
    color: "error",
    bgColor: "#f8d7da",
    textColor: "#721c24",
    icon: "❌"
  },
  Resolved: {
    color: "success",
    bgColor: "#d1e7dd",
    textColor: "#0f5132",
    icon: "✅"
  }
};

const StatusChip = ({ status }) => {
  const config = statusConfig[status] || statusConfig.Pending;

  return (
    <Chip
      label={
        <Box display="flex" alignItems="center" gap={0.5}>
          <span>{config.icon}</span>
          <Typography variant="caption" fontWeight={600}>
            {status}
          </Typography>
        </Box>
      }
      size="small"
      sx={{
        backgroundColor: config.bgColor,
        color: config.textColor,
        fontWeight: 600,
        borderRadius: "8px",
        border: `1px solid ${config.textColor}20`,
        "& .MuiChip-label": {
          padding: "4px 8px"
        }
      }}
    />
  );
};

const UserAvatar = ({ img, name, fallback }) => {
  const src = useSignedUrl(
    img,
    process.env.REACT_APP_BACKEND_PROFILE_IMAGE_LOCATION
  );

  return (
    <Box display="flex" alignItems="center" gap={2}>
      {img ? (
        <Box
          component="img"
          draggable={false}
          sx={{
            borderRadius: "50%",
            height: 40,
            width: 40,
            userSelect: "none",
            border: "2px solid #e5e7eb",
            objectFit: "cover",
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.1)"
            }
          }}
          src={src}
          alt={`${name}'s avatar`}
        />
      ) : (
        <Avatar
          sx={{
            height: 40,
            width: 40,
            backgroundColor: "#667eea",
            color: "white",
            fontWeight: 600,
            fontSize: "14px"
          }}
        >
          {getInitials(fallback)}
        </Avatar>
      )}
      <Box>
        <Typography
          variant="body2"
          fontWeight={500}
          color="#1f2937"
          sx={{ lineHeight: 1.2 }}
        >
          {name}
        </Typography>
      </Box>
    </Box>
  );
};

const ThreadedReplyView = ({ replies, onEditReply, onDeleteReply }) => {
  if (!replies || replies.length === 0) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        sx={{ mb: 1.5, fontWeight: 600 }}
      >
        Conversation Thread ({replies.length})
      </Typography>
      <Stack spacing={1.5}>
        {replies.map((reply, idx) => (
          <Box
            key={idx}
            sx={{
              p: 2,
              backgroundColor: "#f8fafc",
              borderRadius: "8px",
              border: "1px solid #e5e7eb"
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              {reply.userId?.profilePic ? (
                <Avatar
                  src={reply.userId.profilePic}
                  sx={{ width: 32, height: 32 }}
                />
              ) : (
                <Avatar sx={{ width: 32, height: 32, fontSize: "0.8rem" }}>
                  {getInitials(
                    reply.userId?.displayName || reply.userId?.fullName || "EC"
                  )}
                </Avatar>
              )}
              <Box flex={1}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={0.5}
                >
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="#374151"
                  >
                    {reply.userId?.displayName ||
                      reply.userId?.fullName ||
                      "EC's Answer"}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary">
                      {dayjs.utc(reply.createdAt).format("MMM DD, YYYY")}
                    </Typography>
                    {!reply.userId?._id && (
                      <Tooltip title="Edit reply" arrow>
                        <IconButton
                          size="small"
                          onClick={() => onEditReply(reply)}
                          sx={{
                            width: 24,
                            height: 24,
                            color: "#6b7280",
                            "&:hover": {
                              backgroundColor: "#e5e7eb",
                              color: "#374151"
                            }
                          }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Delete reply" arrow>
                      <IconButton
                        size="small"
                        onClick={() => {
                          onDeleteReply(reply._id);
                        }}
                        sx={{
                          width: 24,
                          height: 24,
                          color: "#6b7280",
                          "&:hover": {
                            backgroundColor: "#e5e7eb",
                            color: "#374151"
                          }
                        }}
                      >
                        <Delete sx={{ fontSize: 16, color: "red" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Stack>
                <Typography
                  variant="body2"
                  color="#1f2937"
                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                >
                  {reply.message}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const ReplyDialog = ({
  open,
  onClose,
  row,
  onSubmit,
  onDeleteMainReply,
  onDeleteThreadedReply,
  onAddThreadedReply,
  onEditThreadedReply
}) => {
  const [replyText, setReplyText] = React.useState(row?.reply || "");
  const [threadedReplyText, setThreadedReplyText] = React.useState("");
  const [isEditingMainReply, setIsEditingMainReply] = React.useState(false);
  const [showThreadInput, setShowThreadInput] = React.useState(false);
  const [editingThreadedReply, setEditingThreadedReply] = React.useState(null);

  React.useEffect(() => {
    setReplyText(row?.reply || "");
    setIsEditingMainReply(!row?.reply);
    setShowThreadInput(false);
    setThreadedReplyText("");
    setEditingThreadedReply(null);
  }, [row, open]);

  const handleMainReplySubmit = () => {
    if (replyText.trim()) {
      onSubmit(replyText);
      setIsEditingMainReply(false);
      setReplyText("");
      onClose();
    }
  };

  const handleThreadedReplySubmit = () => {
    if (threadedReplyText.trim()) {
      if (editingThreadedReply) {
        onEditThreadedReply(editingThreadedReply._id, threadedReplyText);
        setEditingThreadedReply(null);
      } else {
        onAddThreadedReply(threadedReplyText);
      }
      setThreadedReplyText("");
      setShowThreadInput(false);
      setTimeout(() => onClose(), 2000);
    }
  };

  const handleEditThreadedReply = (reply) => {
    setEditingThreadedReply(reply);
    setThreadedReplyText(reply.message);
    setShowThreadInput(true);
    setTimeout(() => {
      const elem = document.getElementById("reply-dialog-content");
      elem.scrollTop = elem.scrollHeight;
    }, 500);
  };

  const handleCancelThreadedReply = () => {
    setShowThreadInput(false);
    setThreadedReplyText("");
    setEditingThreadedReply(null);
  };

  const hasMainReply = Boolean(row?.reply);
  const hasThreadedReplies =
    row?.replies && row.replies.filter((item) => item.userId?._id)?.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <ForumIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Question & Answer Thread
            </Typography>
          </Box>
          {hasThreadedReplies && (
            <Chip
              label={`${
                row.replies.filter((item) => item.userId?._id)?.length
              } ${
                row.replies.filter((item) => item.userId?._id)?.length === 1
                  ? "reply"
                  : "replies"
              }`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent id="reply-dialog-content">
        <Box sx={{ mt: 2 }}>
          {/* Original Question */}
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Question from {row?.userId?.displayName || row?.userId?.fullName}:
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: "#fef3c7",
              borderRadius: "8px",
              border: "1px solid #fbbf24",
              mb: 3
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
            >
              {row?.question}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Asked on {dayjs.utc(row?.createdAt).format("MMM DD, YYYY")}
            </Typography>
          </Box>

          {/* Main Reply Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="subtitle2" color="text.secondary">
                Your Main Answer:
              </Typography>
              <Box display="flex" gap={1}>
                {hasMainReply && (
                  <Button
                    size="small"
                    startIcon={<Delete sx={{ color: "red" }} />}
                    onClick={() => {
                      onDeleteMainReply(row);
                      onClose();
                    }}
                    sx={{ textTransform: "none", color: "red" }}
                  >
                    Delete Answer
                  </Button>
                )}
                {hasMainReply && !isEditingMainReply && (
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditingMainReply(true)}
                    sx={{ textTransform: "none" }}
                  >
                    Edit Answer
                  </Button>
                )}
              </Box>
            </Box>

            {isEditingMainReply ? (
              <>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder="Type your answer here..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px"
                    },
                    "& .MuiInputBase-input": {
                      whiteSpace: "pre-wrap"
                    }
                  }}
                />
                <Box display="flex" gap={1} mt={1} justifyContent="flex-end">
                  {hasMainReply && (
                    <Button
                      size="small"
                      onClick={() => {
                        setReplyText(row?.reply);
                        setIsEditingMainReply(false);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleMainReplySubmit}
                    disabled={!replyText.trim()}
                    sx={{ textTransform: "none" }}
                  >
                    {hasMainReply ? "Update Answer" : "Submit Answer"}
                  </Button>
                </Box>
              </>
            ) : (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "#dbeafe",
                  borderRadius: "8px",
                  border: "1px solid #3b82f6"
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}
                >
                  {row?.reply}
                </Typography>
                {row?.repliedAt && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Answered on{" "}
                    {dayjs.utc(row.repliedAt).format("MMM DD, YYYY")}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Threaded Replies */}
          <ThreadedReplyView
            replies={row?.replies}
            onEditReply={handleEditThreadedReply}
            onDeleteReply={(replyId) => {
              onDeleteThreadedReply(row, replyId);
              onClose();
            }}
          />

          {/* Add/Edit Threaded Reply Section */}
          {hasMainReply && (
            <Box sx={{ mt: 3 }}>
              {!showThreadInput ? (
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ReplyIcon />}
                  onClick={() => setShowThreadInput(true)}
                  sx={{
                    textTransform: "none",
                    borderStyle: "dashed",
                    borderWidth: 2,
                    py: 1.5,
                    "&:hover": {
                      borderStyle: "dashed",
                      borderWidth: 2
                    }
                  }}
                >
                  Add Follow-up Reply
                </Button>
              ) : (
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    {editingThreadedReply
                      ? "Edit Reply:"
                      : "Add Follow-up Reply:"}
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={threadedReplyText}
                    onChange={(e) => setThreadedReplyText(e.target.value)}
                    placeholder="Type your follow-up reply here..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px"
                      },
                      "& .MuiInputBase-input": {
                        whiteSpace: "pre-wrap"
                      }
                    }}
                  />
                  <Box display="flex" gap={1} mt={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      onClick={handleCancelThreadedReply}
                      sx={{ textTransform: "none" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<SendIcon />}
                      onClick={handleThreadedReplySubmit}
                      disabled={!threadedReplyText.trim()}
                      sx={{ textTransform: "none" }}
                    >
                      {editingThreadedReply ? "Update Reply" : "Send Reply"}
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReplyButton = ({ row, onReply }) => {
  const hasReply = Boolean(row?.reply);
  const hasThreads =
    row?.replies && row.replies.filter((item) => item.userId?._id)?.length > 0;
  const isPending = row.status === "Pending";
  const lastMissingIndex = row?.replies
    .map((r, i) => (!r.userId?._id ? i : -1))
    .filter((i) => i !== -1)
    .pop();
  let count = 0;
  // if none missing, count = 0
  if (lastMissingIndex === undefined) {
    count = 0;
  } else {
    count = row?.replies.length - (lastMissingIndex + 1);
  }

  return (
    <Tooltip
      title={
        hasThreads
          ? `View conversation (${
              row.replies.filter((item) => item.userId?._id)?.length
            } replies)`
          : hasReply
          ? "View/Edit answer"
          : "Add answer"
      }
      arrow
    >
      <Box sx={{ position: "relative", display: "inline-block" }}>
        <IconButton
          onClick={() => onReply(row)}
          sx={{
            height: "36px",
            width: "36px",
            backgroundColor: hasReply
              ? "#dbeafe"
              : isPending
              ? "#f3e8ff"
              : "#f3f4f6",
            color: hasReply ? "#2563eb" : isPending ? "#7c3aed" : "#9ca3af",
            borderRadius: "8px",
            border: hasReply
              ? "1px solid #2563eb30"
              : isPending
              ? "1px solid #7c3aed30"
              : "1px solid #e5e7eb",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: hasReply
                ? "#bfdbfe"
                : isPending
                ? "#e9d5ff"
                : "#e5e7eb",
              transform: "translateY(-1px)",
              boxShadow:
                hasReply || isPending
                  ? "0 4px 12px rgba(99, 102, 241, 0.15)"
                  : "none"
            }
          }}
          size="small"
          aria-label={hasReply ? "view reply" : "add reply"}
        >
          {hasThreads ? (
            <ForumIcon fontSize="small" />
          ) : hasReply ? (
            <VisibilityIcon fontSize="small" />
          ) : (
            <ReplyIcon fontSize="small" />
          )}
        </IconButton>
        {hasThreads && count ? (
          <Box
            sx={{
              position: "absolute",
              top: -6,
              right: -6,
              backgroundColor: "#dc2626",
              color: "white",
              borderRadius: "50%",
              width: 18,
              height: 18,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "10px",
              fontWeight: 600,
              border: "2px solid white"
            }}
          >
            {count}
            {/* {row.replies.filter((item) => item.userId?._id)?.length} */}
          </Box>
        ) : null}
      </Box>
    </Tooltip>
  );
};

const ActionButtons = ({ row, handleReplyClick, onReject }) => {
  const isPending = row.status === "Pending";

  return (
    <Box display="flex" justifyContent="center" gap={1}>
      <ReplyButton row={row} onReply={handleReplyClick} />

      <Tooltip title="Ignore Question" arrow>
        <IconButton
          onClick={() => onReject && onReject(row)}
          disabled={!isPending}
          sx={{
            height: "36px",
            width: "36px",
            backgroundColor: isPending ? "#fef2f2" : "#f3f4f6",
            color: isPending ? "#dc2626" : "#9ca3af",
            borderRadius: "8px",
            border: isPending ? "1px solid #dc262630" : "1px solid #e5e7eb",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: isPending ? "#fee2e2" : "#f3f4f6",
              transform: isPending ? "translateY(-1px)" : "none",
              boxShadow: isPending
                ? "0 4px 12px rgba(220, 38, 38, 0.15)"
                : "none"
            },
            "&:disabled": {
              cursor: "not-allowed"
            }
          }}
          size="small"
          aria-label="reject"
        >
          <CancelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const StickyHeadTable = () => {
  const [page, setNewPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [replyDialogOpen, setReplyDialogOpen] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const dispatch = useDispatch();
  const loading = useSelector(selectLoading);
  const allQuestionsData = useSelector(selectAllQuestions) || [];
  const totalCount = useSelector(selectTotal) || 0;

  React.useEffect(() => {
    dispatch(getAllQuestionsData({ page, limit: rowsPerPage }));
  }, [page, rowsPerPage, dispatch]);

  const handleChangePage = (event, newPage) => {
    setNewPage(newPage);
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setNewPage(0);
    dispatch(setPage(0));
    dispatch(setLimit(+event.target.value));
  };

  const handleReject = (row) => {
    console.log("Rejecting question:", row);
    dispatch(updateQuestionStatus({ status: "Ignored", questionId: row?._id }));
    dispatch(getAllQuestionsData({ page, limit: rowsPerPage }));
  };

  const handleReplyClick = (row) => {
    setSelectedRow(row);
    setReplyDialogOpen(true);
  };

  const handleReplySubmit = (replyText) => {
    dispatch(
      addReplyToQuestion({ questionId: selectedRow._id, reply: replyText })
    ).then(() => {
      dispatch(getAllQuestionsData({ page, limit: rowsPerPage }));
    });
  };

  const handleThreadedReplySubmit = (message) => {
    dispatch(
      addThreadedReplyToQuestion({ questionId: selectedRow._id, message })
    ).then(() => {
      dispatch(getAllQuestionsData({ page, limit: rowsPerPage }));
      const updatedRow = allQuestionsData.find(
        (q) => q._id === selectedRow._id
      );
      if (updatedRow) {
        setSelectedRow(updatedRow);
      }
    });
  };

  const handleEditThreadedReply = (replyId, message) => {
    dispatch(
      editThreadedReplyToQuestion({
        questionId: selectedRow._id,
        replyId,
        message
      })
    ).then(() => {
      dispatch(getAllQuestionsData({ page, limit: rowsPerPage }));
      const updatedRow = allQuestionsData.find(
        (q) => q._id === selectedRow._id
      );
      if (updatedRow) {
        setSelectedRow(updatedRow);
      }
    });
  };
  return (
    <Paper sx={{ ...styles.paperContainer, width: "100%", mt: 4 }}>
      <TableContainer sx={styles.tableContainer}>
        <Table stickyHeader aria-label="enhanced faq table">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  sx={{
                    ...styles.headerCell,
                    minWidth: column.minWidth,
                    width: column.width
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow hover role="checkbox" tabIndex={0}>
                <TableCell colSpan={12} align="center" sx={{ height: "100%" }}>
                  {/* Center the CircularProgress */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%" // Ensure the Box takes up full height of the row
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              allQuestionsData?.map((row, index) => (
                <TableRow
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  key={row._id || index}
                  sx={styles.tableRow}
                >
                  <TableCell sx={styles.cellContent} align="left">
                    <Box>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="#1f2937"
                      >
                        {dayjs(row.createdAt)?.format("MMM DD, YYYY")}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell sx={styles.cellContent} align="left">
                    <UserAvatar
                      img={row?.userId?.profilePic}
                      fallback={
                        row?.userId?.displayName || row?.userId?.fullName
                      }
                      name={`${
                        row?.userId?.displayName || row?.userId?.fullName
                      } (${row?.userId?.email})`}
                    />
                  </TableCell>

                  <TableCell sx={styles.cellContent} align="left">
                    <Typography
                      variant="body2"
                      color="#374151"
                      sx={{
                        lineHeight: 1.4,
                        maxWidth: "300px",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                      }}
                    >
                      {row.question}
                    </Typography>
                  </TableCell>

                  <TableCell sx={styles.cellContent} align="center">
                    <StatusChip status={row?.status} />
                  </TableCell>

                  <TableCell sx={styles.cellContent} align="center">
                    <ActionButtons
                      row={row}
                      handleReplyClick={handleReplyClick}
                      onReject={handleReject}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          borderTop: "1px solid #e5e7eb",
          backgroundColor: "#fafbfc"
        }}
      >
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            "& .MuiTablePagination-toolbar": {
              paddingLeft: "24px",
              paddingRight: "24px"
            },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
              {
                fontSize: "14px",
                color: "#6b7280"
              }
          }}
        />
      </Box>
      <ReplyDialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        row={selectedRow}
        onSubmit={handleReplySubmit}
        onAddThreadedReply={handleThreadedReplySubmit}
        onEditThreadedReply={handleEditThreadedReply}
        onDeleteMainReply={(row) => {
          dispatch(deleteReplyToQuestion({ questionId: row._id }));
          setTimeout(
            () => dispatch(getAllQuestionsData({ page, limit: rowsPerPage })),
            2000
          );
        }}
        onDeleteThreadedReply={(row, replyId) => {
          dispatch(
            deleteThreadedReplyToQuestion({ questionId: row._id, replyId })
          );
          setTimeout(
            () => dispatch(getAllQuestionsData({ page, limit: rowsPerPage })),
            2000
          );
        }}
      />
    </Paper>
  );
};

export default StickyHeadTable;
